import { useMemo } from 'react';
import { AxiosError, AxiosRequestHeaders, RawAxiosRequestHeaders } from 'axios';

import { CustomObject, RequestParams, RestMethods } from '../types';
import { useLanguage } from './useLanguage';
import { api } from '../services/api';
import { useLocale } from './useLocale';

type Rest = {
    consume: <T>(
        method: RestMethods,
        endpoint: string,
        data?: Record<string, string | number | boolean | CustomObject> | undefined | FormData,
        headers?: AxiosRequestHeaders | RawAxiosRequestHeaders | undefined,
        postParams?: RequestParams,
    ) => Promise<T>;
    getHttpError: (error: AxiosError) => string | { message: string; details: string };
};

export const useRest = (): Rest => {
    const { language, t } = useLanguage();

    const httpErrorLabels = useMemo(
        () => ({
            httpError400: t('httpError.400'),
            httpError401: t('httpError.401'),
            httpError403: t('httpError.403'),
            httpError404: t('httpError.404'),
            httpError405: t('httpError.405'),
            httpError409: t('httpError.409'),
            httpError498: t('httpError.498'),
            httpError500: t('httpError.500'),
            httpError502: t('httpError.502'),
            httpError503: t('httpError.503'),
            httpError504: t('httpError.504'),
            httpErrorDefault: t('An error occurred while processing the request'),
        }),
        [language],
    );

    const errorCodeLabels = useLocale({
        //Organizations
        '#100001': 'errorCodes.#100001',
        '#100002': 'errorCodes.#100002',

        //Company
        '#200001': 'errorCodes.#200001',
        '#200002': 'errorCodes.#200002',
        '#200003': 'errorCodes.#200003',
        '#200004': 'errorCodes.#200004',

        //Menu Options
        '#300001': 'errorCodes.#300001',
        '#300002': 'errorCodes.#300002',
        '#300003': 'errorCodes.#300003',

        //Templates
        '#400001': 'errorCodes.#400001',

        //Plans
        '#500001': 'errorCodes.#500001',
        '#500002': 'errorCodes.#500002',
        '#500003': 'errorCodes.#500003',

        //Users
        '#600003': 'errorCodes.#600003',
    });

    const getHttpError = (error: AxiosError) => {
        const statusCode = (error.response && error.response.status) || 'Default';

        if (Number(statusCode) >= 500 || Number(statusCode) > 400) {
            return httpErrorLabels[`httpError${statusCode}` as keyof object];
        }

        const data = error.response?.data as CustomObject;
        const code = data?.code;
        const message = data?.message;
        const details = data?.details;

        return errorCodeLabels[code] || { message, details };
    };

    const consume = <T>(
        method: RestMethods,
        endpoint: string,
        data?: Record<string, string | number | boolean | CustomObject> | FormData | undefined,
        headers?: AxiosRequestHeaders | RawAxiosRequestHeaders | undefined,
        postParams?: RequestParams,
    ): Promise<T> => {
        const _headers = headers as unknown as AxiosRequestHeaders;

        return new Promise<T>((resolve, reject) => {
            (async () => {
                try {
                    switch (method) {
                        case 'get': {
                            const result = await api.get<T>(endpoint, {
                                params: data,
                                headers: _headers,
                            });
                            resolve(result.data);
                            break;
                        }
                        case 'post': {
                            const result = await api.post<T>(endpoint, data, {
                                headers: _headers,
                                params: postParams,
                            });
                            resolve(result.data);
                            break;
                        }
                        case 'put': {
                            const result = await api.put<T>(endpoint, data, {
                                headers: _headers,
                            });
                            resolve(result.data);
                            break;
                        }
                        case 'remove': {
                            const result = await api.delete<T>(endpoint, {
                                data,
                                headers: _headers,
                            });
                            resolve(result.data);
                            break;
                        }
                        default:
                            reject(httpErrorLabels.httpErrorDefault);
                            break;
                    }
                } catch (error) {
                    reject(getHttpError(error as AxiosError));
                }
            })();
        });
    };

    return {
        consume,
        getHttpError,
    };
};
